import styled from "styled-components";
import {
  getChildren,
  getName,
} from "features/configuration/GeneralConfiguration/utils";
import { ConfigurationItem } from "features/configuration/GeneralConfiguration/ConfigurationItem";
import React, { useContext } from "react";
import {
  GeneralConfigurationContext,
  ModalItemContext,
} from "features/configuration/GeneralConfiguration/generalConfigurationContexts";
import Title from "common/Typography/Title";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { useGetTooltipConfigForField } from "features/configuration/GeneralConfiguration/ConfigurationItem/ConfigurationItemTooltipsConfig";
import { typography } from "@shared/common/Themes/typography";
import EmptyView from "common/Empty/EmptyView";
import { EmptyStatesType } from "common/Icons/types.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { TranslationKeys } from "@shared/common/i18n/types.ts";

const HeaderItem = ({ item, action, emptyDataText, ...props }) => {
  const children = getChildren(item);
  const name = getName(item);
  const { showOverrides, useCard } = useContext(GeneralConfigurationContext)!;
  const { inModal } = useContext(ModalItemContext)!;
  const { getHelperTooltipProps } = useGetTooltipConfigForField();
  const tooltipProps = getHelperTooltipProps(name);
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);

  const headerComponent = (
    <>
      <Wrapper $inModal={inModal}>
        <SectionHeader $weight={typography.fontWeights.BOLD} level={3}>
          <div style={{ display: "flex" }}>
            {name}
            {tooltipProps && (
              <HelperTooltip
                {...tooltipProps}
                description={t(tooltipProps.description)}
                title={t(tooltipProps.title)}
              />
            )}
          </div>
        </SectionHeader>
        {action}
        {!inModal && showOverrides && <Title level={4}>Override</Title>}
      </Wrapper>
      {children.map((item, index) => (
        <ConfigurationItem key={index} item={item} {...props} />
      ))}
      {children.length === 0 && emptyDataText && (
        <EmptyWrapper>
          <EmptyView
            iconKey={EmptyStatesType.Empty}
            iconWidth={100}
            description={emptyDataText}
            style={{ padding: "36px 0" }}
          />
        </EmptyWrapper>
      )}
    </>
  );

  return inModal || !useCard ? (
    headerComponent
  ) : (
    <SectionWrapper>{headerComponent}</SectionWrapper>
  );
};

export default HeaderItem;

export const SectionWrapper = styled.div`
  background: ${(p) => p.theme.colors.cardBgColor};
  border-radius: 12px;
  padding: 20px;
  border: 0.2px solid #8992a345;
  ${(p) =>
    !p.theme.isDarkMode &&
    `
  box-shadow: ${p.theme.shadows.card};
`}
`;
const Wrapper = styled.div<{ $inModal?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  ${(props) =>
    props.$inModal &&
    ` 
    margin-top: 20px;
  `}
`;
const SectionHeader = styled(Title)`
  color: ${(p) => p.theme.colors.titleColor};
`;
const EmptyWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;
